import { BasicLocalCoords, RotationInfo, Vertices } from '../../../types/webPlayer';

/**
 * Determina se i vertici del rettangolo sono in senso orario
 * @param {Object} A - Vertice A {x, y}
 * @param {Object} B - Vertice B {x, y}
 * @param {Object} C - Vertice C {x, y}
 * @param {Object} D - Vertice D {x, y}
 * @returns {boolean} - true se in senso orario, false se antiorario
 */
export const isClockwise = (
  A: BasicLocalCoords,
  B: BasicLocalCoords,
  C: BasicLocalCoords,
): boolean => {
  const cross = (B.x - A.x) * (C.y - A.y) - (B.y - A.y) * (C.x - A.x);
  return cross < 0;
};

/**
 * Calcola l'angolo di rotazione necessario per allineare il rettangolo
 * @param {Object} A - Vertice A {x, y}
 * @param {Object} B - Vertice B {x, y}
 * @param {Object} C - Vertice C {x, y}
 * @param {Object} D - Vertice D {x, y}
 * @returns {Object} - {angle: angolo in radianti, isClockwise: boolean}
 */
export const calculateRotationAngle = (
  A: BasicLocalCoords,
  B: BasicLocalCoords,
  C: BasicLocalCoords,
): {angle: number, isClockwise: boolean} => {
  const clockwise = isClockwise(A, B, C);

  if (clockwise) {
    const dx = B.x - A.x;
    const dy = B.y - A.y;
    const angle = Math.atan2(-dx, dy);
    return { angle, isClockwise: true };
  }

  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const angle = Math.atan2(dx, -dy);
  return { angle, isClockwise: false };
};

/**
 * Ruota un punto attorno a un centro specificato di un determinato angolo.
 *
 * @param {Object} point - Il punto da ruotare, rappresentato come un oggetto con le proprietà `x` e `y`.
 * @param {number} centerX - La coordinata x del centro di rotazione.
 * @param {number} centerY - La coordinata y del centro di rotazione.
 * @param {number} angle - L'angolo di rotazione in radianti.
 * @return {Object} Le nuove coordinate del punto dopo la rotazione,
 * rappresentate come un oggetto con le proprietà `x` e `y`.
 */
export const rotatePoint = (point: BasicLocalCoords, centerX: number, centerY: number, angle: number): object => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const dx = point.x - centerX;
  const dy = point.y - centerY;

  return {
    x: centerX + dx * cos - dy * sin,
    y: centerY + dx * sin + dy * cos,
  };
};

/**
 * Calcola il centro di un rettangolo utilizzando solo tre vertici A, B e C
 * @param {BasicLocalCoords} A - Primo vertice {x, y}
 * @param {BasicLocalCoords} B - Secondo vertice {x, y}
 * @param {BasicLocalCoords} C - Terzo vertice {x, y}
 * @returns {BasicLocalCoords} Centro del rettangolo {x, y}
 */
export const computeRectangleCenter = (
  A: BasicLocalCoords,
  C: BasicLocalCoords,
): BasicLocalCoords => {
  return {
    x: (A.x + C.x) / 2,
    y: (A.y + C.y) / 2,
  };
}

/**
 * Ruota il rettangolo per allinearlo correttamente
 * @param {Vertices} vertices - {A, B, C, D} con coordinate {x, y}
 * @returns {vertices: Vertices, rotationInfo: RotationInfo} - {vertices: vertici ruotati, rotationInfo: rotazione}
 */
export const rotateRectangle = (vertices: Vertices): {vertices: Vertices, rotationInfo: RotationInfo} => {
  const {
    A, B, C, D,
  } = vertices;

  const centro = computeRectangleCenter(A, C);

  // Calcola l'angolo di rotazione
  const rotationInfo = calculateRotationAngle(A, B, C);
  let angle = -rotationInfo.angle; // Negativo per correggere la rotazione
  angle = Object.is(angle, -0) ? 0 : angle;

  // Ruota tutti i vertici
  const rotatedA = rotatePoint(A, centro.x, centro.y, angle);
  const rotatedB = rotatePoint(B, centro.x, centro.y, angle);
  const rotatedC = rotatePoint(C, centro.x, centro.y, angle);
  const rotatedD = rotatePoint(D, centro.x, centro.y, angle);

  // Ordina i vertici nella posizione corretta
  let finalVertices: Vertices;

  if (rotationInfo.isClockwise) {
    // A basso-sinistra, B alto-sinistra, C alto-destra, D basso-destra
    finalVertices = {
      A: rotatedA, // basso-sinistra
      B: rotatedB, // alto-sinistra
      C: rotatedC, // alto-destra
      D: rotatedD, // basso-destra
    } as Vertices;
  } else {
    // A alto-sinistra, B basso-sinistra, C basso-destra, D alto-destra
    finalVertices = {
      A: rotatedA, // alto-sinistra
      B: rotatedB, // basso-sinistra
      C: rotatedC, // basso-destra
      D: rotatedD, // alto-destra
    } as Vertices;
  }

  return {
    vertices: finalVertices,
    rotationInfo: {
      angle,
      centerX: centro.x,
      centerY: centro.y,
      isClockwise: rotationInfo.isClockwise,
    },
  };
};

/**
 * Ruota il marker in base alla rotazione applicata al rettangolo
 * @param {BasicLocalCoords} marker - Coordinate del marker {x, y}
 * @param {number} angle
 * @param {BasicLocalCoords} center
 * @returns {BasicLocalCoords} - Nuove coordinate del marker {x, y}
 */
export const rotateMarker = (marker: BasicLocalCoords, angle: number, center: BasicLocalCoords): BasicLocalCoords => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  const dx = marker.x - center.x;
  const dy = marker.y - center.y;

  return {
    x: center.x + dx * cos - dy * sin,
    y: center.y + dx * sin + dy * cos,
  };
};

export const getScale = (
  width: number,
  height: number,
  winWidth: number,
  winHeight: number,
) => {
  const scaleX = winWidth / width;
  const scaleY = winHeight / height;
  return Math.min(scaleX, scaleY);
}
